// TUT-450 — as perícias: lista, assistente, edição, os três gestos de encerramento e os seus motivos, criação, relatório recebido, admissão e as reuniões da agenda.
import { demoApi } from "../demo-data";
import type {
  CreateExpertiseInput,
  ExpertiseCloseResponse,
  ExpertiseClosureOutcome,
  ExpertiseClosureReason,
  ExpertiseStructuredClosureInput,
  ExpertiseListResponse,
  RescheduleExpertiseInput,
  SaveExpertiseClosureReasonInput,
  ExpertiseSummary,
  ExpertiseIntake,
  MeetingListResponse,
  CreateMeetingInput,
  MeetingSummary,
  ExpertReport,
  IntakeBlockKey,
  IntakeStatusKey,
  UpdateExpertiseInput,
  DataDoJuizoInput,
  ResultadoDaPericiaInput,
  ResultadoDaPericiaResponse,
} from "../types";
import {
  comFatia,
  demoAccountsPayable,
  demoLocalRepresentativeFinancialTerms,
  request,
  sha256,
} from "./infra";
import { ApiError } from "../api-error";
import { enviarAoArmazenamento, type RelatorDeProgresso } from "../upload-transport";
import type { ExpertiseListQuery } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDasPericias = {
  expertises(
    filtros: ExpertiseListQuery = {},
  ): Promise<ExpertiseListResponse> {
    if (useDemoApi) return demoApi.expertises(filtros);
    const params = new URLSearchParams();
    if (filtros.search) params.set("search", filtros.search);
    for (const status of filtros.statuses ?? []) {
      params.append("status", status);
    }
    if (filtros.subjectId) params.set("subjectId", filtros.subjectId);
    if (filtros.clientOrganizationId)
      params.set("clientOrganizationId", filtros.clientOrganizationId);
    if (filtros.location) params.set("location", filtros.location);
    if (filtros.assistente) params.set("assistente", filtros.assistente);
    if (filtros.formulario) params.set("formulario", filtros.formulario);
    // TUT-527 (D32) — "Atribuídas a mim".
    if (filtros.mine) params.set("mine", "true");
    // TUT-476 — o motivo de encerramento e o período de agendamento.
    if (filtros.motivo) params.set("motivo", filtros.motivo);
    if (filtros.periodoDe) params.set("periodoDe", filtros.periodoDe);
    if (filtros.periodoAte) params.set("periodoAte", filtros.periodoAte);
    // TUT-204c: `comFatia` no lugar da montagem à mão — ver `ExpertiseListQuery`.
    return request<ExpertiseListResponse>(
      `/expertises${comFatia(params, filtros)}`,
    );
  },
  /** TUT-309 — "Indicar assistente" (ou tirar, com `null`). */
  setExpertiseAssistant(
    expertiseId: string,
    assistantId: string | null,
  ): Promise<ExpertiseSummary> {
    if (useDemoApi) return demoApi.setExpertiseAssistant(expertiseId, assistantId);
    return request<ExpertiseSummary>(
      `/expertises/${encodeURIComponent(expertiseId)}/assistant`,
      { method: "PUT", body: JSON.stringify({ assistantId }) },
    );
  },
  // TUT-279 fase 2 — a reunião, o segundo tipo de compromisso da agenda ao
  // lado da perícia. Só dois métodos, no molde exato de `expertises`/
  // `createExpertise`: a fase 1 da API não tem edição nem exclusão (o mínimo
  // é o mínimo — ver `apps/api/src/domain/meetings.ts`).
  meetings(): Promise<MeetingListResponse> {
    if (useDemoApi) return demoApi.meetings();
    return request<MeetingListResponse>("/meetings");
  },
  createMeeting(input: CreateMeetingInput): Promise<MeetingSummary> {
    if (useDemoApi) return demoApi.createMeeting(input);
    return request<MeetingSummary>("/meetings", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateExpertise(
    id: string,
    input: UpdateExpertiseInput,
  ): Promise<ExpertiseSummary> {
    if (useDemoApi) return demoApi.updateExpertise(id, input).then((saved) => {
      const representativeId = saved.localRepresentativeId;
      const terms = representativeId ? demoLocalRepresentativeFinancialTerms.get(representativeId) : undefined;
      if (saved.status.key === "scheduled" && representativeId && terms && !demoAccountsPayable.some((item) => item.expertiseId === saved.id)) {
        demoAccountsPayable.push({ id: crypto.randomUUID(), expertiseId: saved.id, processId: saved.processId, processNumber: saved.processNumber, representativeId, representativeName: saved.localRepresentativeName ?? "Representante local", origin: "AUTOMATIC", description: null, notes: null, supplierId: null, payeeName: saved.localRepresentativeName ?? "Representante local", payeeKind: "REPRESENTATIVE", contractedAmountCents: terms.contractedAmountCents, excessKmAmountCents: terms.excessKmAmountCents, paymentTerms: terms.paymentTerms, status: "AWAITING_DELIVERABLES", dueDate: null, deliverables: { technicalReport: null, invoiceReceipt: null }, cancellationReason: null, paidAt: null, paidByName: null, cancelledAt: null, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
      }
      return saved;
    });
    return request<ExpertiseSummary>(`/expertises/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  // Gestos de ocorrência da perícia (TUT-035): remarcar cria a ocorrência
  // seguinte vinculada; frustrar devolve à fila; cancelar encerra.
  rescheduleExpertise(
    id: string,
    input: RescheduleExpertiseInput,
  ): Promise<ExpertiseCloseResponse> {
    if (useDemoApi) return demoApi.rescheduleExpertise(id, input);
    return request<ExpertiseCloseResponse>(
      `/expertises/${encodeURIComponent(id)}/reschedule`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  frustrateExpertise(
    id: string,
    input: ExpertiseStructuredClosureInput,
  ): Promise<ExpertiseCloseResponse> {
    if (useDemoApi) return demoApi.frustrateExpertise(id, input);
    return request<ExpertiseCloseResponse>(
      `/expertises/${encodeURIComponent(id)}/frustrate`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  cancelExpertise(id: string, reason: string): Promise<ExpertiseCloseResponse> {
    if (useDemoApi) return demoApi.cancelExpertise(id, reason);
    return request<ExpertiseCloseResponse>(
      `/expertises/${encodeURIComponent(id)}/cancel`,
      { method: "POST", body: JSON.stringify({ reason }) },
    );
  },
  /** TUT-527 — o pop-up do resultado da perícia (D13, D14/D16, D38, D39). */
  registrarResultadoDaPericia(
    id: string,
    input: ResultadoDaPericiaInput,
  ): Promise<ResultadoDaPericiaResponse> {
    if (useDemoApi) return demoApi.registrarResultadoDaPericia(id, input);
    return request<ResultadoDaPericiaResponse>(
      `/expertises/${encodeURIComponent(id)}/resultado`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  /** TUT-527 — "Informar data": a perícia sem data vai para "Pendente agendamento". */
  informarDataDoJuizo(id: string, input: DataDoJuizoInput): Promise<ExpertiseSummary> {
    if (useDemoApi) return demoApi.informarDataDoJuizo(id, input);
    return request<ExpertiseSummary>(
      `/expertises/${encodeURIComponent(id)}/data-do-juizo`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  /**
   * TUT-527 (D13) — o anexo do "Relatório finalizado", pela porta do próprio
   * resultado (o assistente interno não tem `documentos.gerir`). O mesmo
   * transporte do envio de documento: checksum, URL pré-assinada e conclusão.
   */
  async enviarRelatorioDaPericia(
    id: string,
    arquivo: File,
    aoProgredir?: RelatorDeProgresso,
  ): Promise<{ id: string }> {
    aoProgredir?.({ fase: "preparando" });
    if (useDemoApi) {
      const documento = await demoApi.enviarRelatorioDaPericia(id, arquivo);
      aoProgredir?.({ fase: "concluindo" });
      return documento;
    }
    const base = `/expertises/${encodeURIComponent(id)}/relatorio/uploads`;
    const preparado = await request<{
      uploadId: string;
      upload: { url: string; method: "PUT"; headers: Record<string, string> };
    }>(base, {
      method: "POST",
      body: JSON.stringify({
        originalName: arquivo.name,
        mimeType: arquivo.type,
        sizeBytes: arquivo.size,
        checksumSha256: await sha256(arquivo),
      }),
    });
    const resposta = await enviarAoArmazenamento({
      url: preparado.upload.url,
      method: preparado.upload.method,
      headers: preparado.upload.headers,
      corpo: arquivo,
      aoProgredir,
    });
    if (!resposta.ok) {
      throw new ApiError("O armazenamento recusou o arquivo. Tente novamente.", resposta.status);
    }
    aoProgredir?.({ fase: "concluindo" });
    return request<{ id: string }>(`${base}/${encodeURIComponent(preparado.uploadId)}/complete`, {
      method: "POST",
    });
  },
  expertiseClosureReasons(
    filters: {
      includeInactive?: boolean;
      outcome?: ExpertiseClosureOutcome;
    } = {},
  ): Promise<ExpertiseClosureReason[]> {
    if (useDemoApi) return demoApi.expertiseClosureReasons(filters);
    const params = new URLSearchParams();
    if (filters.includeInactive) params.set("includeInactive", "true");
    if (filters.outcome) params.set("outcome", filters.outcome);
    const query = params.size ? `?${params}` : "";
    return request<ExpertiseClosureReason[]>(
      `/expertise-closure-reasons${query}`,
    );
  },
  createExpertiseClosureReason(
    input: SaveExpertiseClosureReasonInput,
  ): Promise<ExpertiseClosureReason> {
    if (useDemoApi) return demoApi.createExpertiseClosureReason(input);
    return request<ExpertiseClosureReason>("/expertise-closure-reasons", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  versionExpertiseClosureReason(
    reasonId: string,
    input: SaveExpertiseClosureReasonInput,
  ): Promise<ExpertiseClosureReason> {
    if (useDemoApi)
      return demoApi.versionExpertiseClosureReason(reasonId, input);
    return request<ExpertiseClosureReason>(
      `/expertise-closure-reasons/${encodeURIComponent(reasonId)}/versions`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  setExpertiseClosureReasonActive(
    reasonId: string,
    active: boolean,
  ): Promise<ExpertiseClosureReason> {
    if (useDemoApi)
      return demoApi.setExpertiseClosureReasonActive(reasonId, active);
    return request<ExpertiseClosureReason>(
      `/expertise-closure-reasons/${encodeURIComponent(reasonId)}/active`,
      { method: "POST", body: JSON.stringify({ active }) },
    );
  },
  createExpertise(
    processId: string,
    input: CreateExpertiseInput,
  ): Promise<ExpertiseSummary> {
    if (useDemoApi) return demoApi.createExpertise(processId, input).then((saved) => {
      const representativeId = saved.localRepresentativeId;
      const terms = representativeId ? demoLocalRepresentativeFinancialTerms.get(representativeId) : undefined;
      if (saved.status.key === "scheduled" && representativeId && terms) {
        demoAccountsPayable.push({ id: crypto.randomUUID(), expertiseId: saved.id, processId: saved.processId, processNumber: saved.processNumber, representativeId, representativeName: saved.localRepresentativeName ?? "Representante local", origin: "AUTOMATIC", description: null, notes: null, supplierId: null, payeeName: saved.localRepresentativeName ?? "Representante local", payeeKind: "REPRESENTATIVE", contractedAmountCents: terms.contractedAmountCents, excessKmAmountCents: terms.excessKmAmountCents, paymentTerms: terms.paymentTerms, status: "AWAITING_DELIVERABLES", dueDate: null, deliverables: { technicalReport: null, invoiceReceipt: null }, cancellationReason: null, paidAt: null, paidByName: null, cancelledAt: null, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
      }
      return saved;
    });
    return request<ExpertiseSummary>(
      `/processes/${encodeURIComponent(processId)}/expertises`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  },
  /**
   * TUT-291a — "relatório recebido": devolve a perícia (`PENDING_REPORT`) a
   * `COMPLETED` e abre o laudo `DRAFTING`. A resposta é o LAUDO criado — quem
   * invalida a perícia é o chamador, como as outras mutações de perícia.
   */
  reportReceived(expertiseId: string): Promise<ExpertReport> {
    if (useDemoApi) return demoApi.reportReceived(expertiseId);
    return request<ExpertReport>(
      `/expertises/${encodeURIComponent(expertiseId)}/report-received`,
      { method: "POST" },
    );
  },
  /**
   * TUT-314 — o "Desfazer" do "Relatório recebido": cancela o laudo que o
   * gesto abriu (`reportId`, o da resposta) e devolve a perícia a pendente
   * relatório. O servidor recusa (409) se o laudo já andou.
   */
  undoReportReceived(expertiseId: string, reportId: string): Promise<void> {
    if (useDemoApi) return demoApi.undoReportReceived(expertiseId, reportId);
    return request<void>(
      `/expertises/${encodeURIComponent(expertiseId)}/report-received/undo`,
      { method: "POST", body: JSON.stringify({ reportId }) },
    );
  },
  // Roteiro de perícia (TUT-037). O GET CRIA o roteiro quando ainda não
  // existe — o roteiro nasce da perícia, e um botão "criar" seria uma etapa
  // sem decisão nenhuma dentro.
  expertiseIntake(expertiseId: string): Promise<ExpertiseIntake> {
    if (useDemoApi) return demoApi.expertiseIntake(expertiseId);
    return request<ExpertiseIntake>(
      `/expertises/${encodeURIComponent(expertiseId)}/intake`,
    );
  },
  saveIntakeBlock(
    intakeId: string,
    input: {
      blockKey: IntakeBlockKey;
      content: Record<string, unknown>;
      expectedVersion: number;
    },
  ): Promise<ExpertiseIntake> {
    if (useDemoApi) return demoApi.saveIntakeBlock(intakeId, input);
    return request<ExpertiseIntake>(
      `/expertise-intakes/${encodeURIComponent(intakeId)}/blocks`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },
  transitionIntake(
    intakeId: string,
    input: { to: IntakeStatusKey; reason?: string | null },
  ): Promise<ExpertiseIntake> {
    if (useDemoApi) return demoApi.transitionIntake(intakeId, input);
    return request<ExpertiseIntake>(
      `/expertise-intakes/${encodeURIComponent(intakeId)}/transitions`,
      {
        method: "POST",
        body: JSON.stringify({
          to: input.to.toUpperCase(),
          ...(input.reason ? { reason: input.reason } : {}),
        }),
      },
    );
  },
};
